import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { resources } from '../../../core/services';

/**
 * Data behind the High Level Overview block: the project's dashboard-card
 * tabs, the card assignments in them, the pool of cards to pick from, which
 * tab is showing, and the mutations that edit all of that. Same pattern as
 * useMainEntitiesConfig, but assigning DocumentDashboardCard rows instead of
 * entity types.
 */
export function useDashboardCardTabs(projectId: number) {
  const queryClient = useQueryClient();
  const dashboardCardAssignmentsApi = resources.projectDashboardCardAssignmentsFor(projectId);
  const dashboardCardTabsApi = resources.projectDashboardCardTabsFor(projectId);

  const dashboardCardAssignmentsQuery = useQuery({
    queryKey: ['project-dashboard-card-assignments', projectId],
    queryFn: () => dashboardCardAssignmentsApi.list({ pageSize: 500, sort: 'order' }),
    enabled: Number.isFinite(projectId),
  });
  const dashboardCardAssignments = dashboardCardAssignmentsQuery.data?.data ?? [];

  const dashboardCardTabsQuery = useQuery({
    queryKey: ['project-dashboard-card-tabs', projectId],
    queryFn: () => dashboardCardTabsApi.list({ pageSize: 100, sort: 'order' }),
    enabled: Number.isFinite(projectId),
  });
  const dashboardCardTabs = dashboardCardTabsQuery.data?.data ?? [];

  // Every DocumentDashboardCard generated across the project's documents —
  // the pool the "Add card" dropdown picks from.
  const projectDashboardCardsQuery = useQuery({
    queryKey: ['project-dashboard-cards', projectId],
    queryFn: () => resources.documentDashboardCards.list({ filter: { projectId: [projectId] }, pageSize: 500 }),
    enabled: Number.isFinite(projectId),
  });
  const projectDashboardCards = projectDashboardCardsQuery.data?.data ?? [];

  // Which High Level Overview tab is showing (null = the implicit "General" tab).
  const [activeDashboardTabId, setActiveDashboardTabId] = useState<number | null>(null);
  // Falls back to General if the active tab was deleted out from under it.
  useEffect(() => {
    if (activeDashboardTabId !== null && !dashboardCardTabs.some((t) => t.id === activeDashboardTabId)) {
      setActiveDashboardTabId(null);
    }
  }, [dashboardCardTabs, activeDashboardTabId]);

  const invalidateDashboardCardAssignments = () =>
    queryClient.invalidateQueries({ queryKey: ['project-dashboard-card-assignments', projectId] });

  const addDashboardCardAssignment = useMutation({
    mutationFn: ({ documentDashboardCardId, tabId }: { documentDashboardCardId: number; tabId: number | null }) => {
      const nextOrder =
        dashboardCardAssignments.filter((a) => a.tabId === tabId).reduce((max, a) => Math.max(max, a.order), -1) + 1;
      return dashboardCardAssignmentsApi.create({ documentDashboardCardId, tabId, order: nextOrder });
    },
    onSuccess: invalidateDashboardCardAssignments,
  });

  const deleteDashboardCardAssignment = useMutation({
    mutationFn: (id: number) => dashboardCardAssignmentsApi.remove(id),
    onSuccess: invalidateDashboardCardAssignments,
  });

  const invalidateDashboardCardTabs = () =>
    queryClient.invalidateQueries({ queryKey: ['project-dashboard-card-tabs', projectId] });

  const addDashboardCardTab = useMutation({
    mutationFn: ({ name, icon }: { name: string; icon: string | null }) => {
      const nextOrder = dashboardCardTabs.reduce((max, t) => Math.max(max, t.order), -1) + 1;
      return dashboardCardTabsApi.create({ name, icon, order: nextOrder });
    },
    onSuccess: invalidateDashboardCardTabs,
  });

  // A deleted tab's rows fall back to General server-side (tabId set to null
  // via ON DELETE SET NULL), so assignments need a refetch too.
  const deleteDashboardCardTab = useMutation({
    mutationFn: (id: number) => dashboardCardTabsApi.remove(id),
    onSuccess: () => {
      invalidateDashboardCardTabs();
      invalidateDashboardCardAssignments();
    },
  });

  // Reorders cards within a tab by swapping `order` with the adjacent card
  // (by current display order) — same swap pattern as tab reordering above.
  const moveDashboardCardAssignment = useMutation({
    mutationFn: async ({ id, direction }: { id: number; direction: 'before' | 'after' }): Promise<void> => {
      const current = dashboardCardAssignments.find((a) => a.id === id);
      if (!current) return;
      const sorted = [...dashboardCardAssignments]
        .filter((a) => a.tabId === current.tabId)
        .sort((a, b) => a.order - b.order);
      const index = sorted.findIndex((a) => a.id === id);
      const swapIndex = direction === 'before' ? index - 1 : index + 1;
      if (index === -1 || swapIndex < 0 || swapIndex >= sorted.length) return;
      const neighbor = sorted[swapIndex];
      await Promise.all([
        dashboardCardAssignmentsApi.update(current.id, { order: neighbor.order }),
        dashboardCardAssignmentsApi.update(neighbor.id, { order: current.order }),
      ]);
    },
    onSuccess: invalidateDashboardCardAssignments,
  });

  const resizeDashboardCardAssignment = useMutation({
    mutationFn: ({ id, columnSpan }: { id: number; columnSpan: number }) =>
      dashboardCardAssignmentsApi.update(id, { columnSpan }),
    onSuccess: invalidateDashboardCardAssignments,
  });

  return {
    tabs: dashboardCardTabs,
    assignments: dashboardCardAssignments,
    cards: projectDashboardCards,
    isPending:
      dashboardCardTabsQuery.isPending || dashboardCardAssignmentsQuery.isPending || projectDashboardCardsQuery.isPending,
    activeTabId: activeDashboardTabId,
    setActiveTabId: setActiveDashboardTabId,
    addCard: (cardId: number, tabId: number | null) =>
      addDashboardCardAssignment.mutate({ documentDashboardCardId: cardId, tabId }),
    removeCard: (id: number) => deleteDashboardCardAssignment.mutate(id),
    addTab: (name: string, icon: string | null) => addDashboardCardTab.mutateAsync({ name, icon }),
    deleteTab: (id: number) => deleteDashboardCardTab.mutate(id),
    moveCard: (id: number, direction: 'before' | 'after') => moveDashboardCardAssignment.mutate({ id, direction }),
    resizeCard: (id: number, columnSpan: number) => resizeDashboardCardAssignment.mutate({ id, columnSpan }),
  };
}
